import {
    lazyMarkAsyncItemRead,
    getAsyncItemEntry,
    consumeCardShownDuration,
} from 'owa-copilot-async-chat';
import { lazyOpenBizChatSidePane } from 'owa-bizchat-shared';
import { logCoreUsage } from 'owa-analytics';
import type { MailboxInfo } from 'owa-client-types';
import type { BizChatSidePaneSources } from 'owa-bizchat-types';
import { getTimestamp, now } from 'owa-datetime';

export interface OpenAsyncConversationOptions {
    /** Registry entry id (= Sydney conversationId) to load in the sidepane. */
    conversationId: string;
    mailboxInfo: MailboxInfo;
    targetWindow: Window;
    /** Telemetry entry point for the sidepane open (the surface the user opened from). */
    source: BizChatSidePaneSources;
}

/**
 * Single source of truth for the "open an async conversation" handoff, shared by the DAB card stack
 * and the Tasks Hub. Marks the conversation read (optimistic local flip + Sydney persist via
 * `markAsyncItemRead`) and loads it into the BizChat side pane.
 *
 * Lives in this non-UI package (not the React `owa-copilot-async-chat-ui` package) so eager
 * consumers like the chat drawer can invoke the handoff without pulling in the card UI bundle.
 * `lazyMarkAsyncItemRead` keeps the Sydney persist code in the async-chat chunk until first use.
 *
 * Callers layer their own surface-specific steps around this: the DAB dismisses the card from its
 * stack; the drawer flips out of the Tasks Hub view and invalidates the stale post-message handler
 * before calling this. Each caller passes the `source` matching the surface it opened from.
 */
export function openAsyncConversation({
    conversationId,
    mailboxInfo,
    targetWindow,
    source,
}: OpenAsyncConversationOptions): void {
    const entry = getAsyncItemEntry(mailboxInfo, conversationId);
    const isFinished =
        entry?.status === 'Completed' || entry?.status === 'Failed' || entry?.status === 'Canceled';
    const timeSinceFinishedMs =
        isFinished && entry.updateTimeUtc !== undefined
            ? Math.max(0, getTimestamp(now()) - entry.updateTimeUtc)
            : undefined;

    logCoreUsage('AsyncChat_ConversationOpened', mailboxInfo, {
        source,
        status: entry?.status,
        kind: entry?.kind,
        conversationId,
        timeToInteractMs: consumeCardShownDuration(conversationId),
        timeSinceFinishedMs,
    });

    lazyMarkAsyncItemRead.importAndExecute({ id: conversationId, mailboxInfo });
    lazyOpenBizChatSidePane.importAndExecute({
        source,
        conversationId,
        isNewChat: false,
        shouldExecuteQuery: false,
        targetWindow,
        mailboxInfo,
    });
}
